import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import { RecurrenceSettings, ZoomRecurrenceRequest } from '@bariendo/lib/scheduling';
import { ZoomRecurrenceType } from '@bariendo/lib/constants';
import { BlockFormData } from '../Block/types';
import { AppointmentFormData, AppointmentRecurrencePreset, AppointmentRepeatingInterval } from '../types';

dayjs.extend(utc);
dayjs.extend(tz);

/** dayjs weekday (0 = Sunday … 6 = Saturday); placeholder before a date selects the real nth weekday. */
export const DEFAULT_MONTHLY_RECURRENCE_WEEKDAY = 5;

export function toOrdinal(n: number): string {
  const j = n % 10;
  const k = n % 100;
  if (k >= 11 && k <= 13) {
    return `${n}th`;
  }
  if (j === 1) {
    return `${n}st`;
  }
  if (j === 2) {
    return `${n}nd`;
  }
  if (j === 3) {
    return `${n}rd`;
  }
  return `${n}th`;
}

/** Nth occurrence of weekday in the appointment's calendar month (dayjs weekday 0–6). */
export function computeMonthlyNthWeekdayFromDate(
  date: Date,
  timezone: string
): { n: 1 | 2 | 3 | 4 | 5; weekday: number } {
  const d = dayjs(date).tz(timezone);
  const weekday = d.day();
  let first = d.startOf('month');
  while (first.day() !== weekday) {
    first = first.add(1, 'day');
  }
  let n = 1;
  let cursor = first;
  while (!cursor.isSame(d, 'day')) {
    cursor = cursor.add(1, 'week');
    n++;
    if (n > 5) {
      break;
    }
  }
  return { n: Math.min(n, 5) as 1 | 2 | 3 | 4 | 5, weekday };
}

function presetToRepeatingInterval(preset: AppointmentRecurrencePreset): AppointmentRepeatingInterval {
  if (preset === 'daily') {
    return AppointmentRepeatingInterval.DAILY;
  }
  if (preset === 'monthly') {
    return AppointmentRepeatingInterval.MONTHLY;
  }
  return AppointmentRepeatingInterval.WEEKLY;
}

export function nextBlockFormDataWithRecurrencePreset(
  prev: BlockFormData,
  preset: AppointmentRecurrencePreset,
  timezone: string
): BlockFormData {
  const next: BlockFormData = {
    ...prev,
    recurrencePreset: preset,
    isRepeating: preset !== 'none',
  };
  if (preset === 'monthly' && prev.date) {
    const computed = computeMonthlyNthWeekdayFromDate(prev.date, timezone);
    next.monthlyNth = computed.n;
    next.monthlyWeekday = computed.weekday;
  }
  return next;
}

export function nextFormDataWithRecurrencePreset(
  prev: AppointmentFormData,
  preset: AppointmentRecurrencePreset,
  timezone: string
): AppointmentFormData {
  const next: AppointmentFormData = {
    ...prev,
    recurrencePreset: preset,
    isRepeating: preset !== 'none',
    repeatingInterval: presetToRepeatingInterval(preset),
  };
  if (preset === 'monthly' && prev.date) {
    const computed = computeMonthlyNthWeekdayFromDate(prev.date, timezone);
    next.monthlyNth = computed.n;
    next.monthlyWeekday = computed.weekday;
  }
  return next;
}

export function buildRecurrenceSettingsFromForm(formData: AppointmentFormData): RecurrenceSettings | undefined {
  if (formData.recurrencePreset === 'none' || !formData.isRepeating) {
    return undefined;
  }
  const count = formData.repeatTimes;
  switch (formData.recurrencePreset) {
    case 'daily':
      return { frequency: 'daily', interval: 1, count };
    case 'weekly':
      return { frequency: 'weekly', interval: 1, count };
    case 'biweekly':
      return { frequency: 'weekly', interval: 2, count };
    case 'monthly':
      return {
        frequency: 'monthly',
        interval: 1,
        count,
        monthlyNthWeekday: { n: formData.monthlyNth, weekday: formData.monthlyWeekday },
      };
    default:
      return undefined;
  }
}

export function buildBlockRecurrenceFromForm(data: BlockFormData): RecurrenceSettings {
  const count = data.repeatTimes;
  switch (data.recurrencePreset) {
    case 'daily':
      return { frequency: 'daily', interval: 1, count };
    case 'weekly':
      return { frequency: 'weekly', interval: 1, count };
    case 'biweekly':
      return { frequency: 'weekly', interval: 2, count };
    case 'monthly':
      return {
        frequency: 'monthly',
        interval: 1,
        count,
        monthlyNthWeekday: { n: data.monthlyNth, weekday: data.monthlyWeekday },
      };
    default:
      throw new Error('Invalid block recurrence preset');
  }
}

function dayjsDayToZoomWeekday(d: number): number {
  return d === 0 ? 1 : d + 1;
}

export function buildZoomRecurrenceFromForm(formData: AppointmentFormData): ZoomRecurrenceRequest | undefined {
  if (!formData.isRepeating || formData.repeatTimes < 2 || !formData.date || !formData.timezone) {
    return undefined;
  }
  const preset = formData.recurrencePreset;
  if (preset === 'daily') {
    return { type: ZoomRecurrenceType.DAILY, repeat_interval: 1, end_times: formData.repeatTimes };
  }
  if (preset === 'weekly' || preset === 'biweekly') {
    const dow = dayjs(formData.date).tz(formData.timezone).day();
    return {
      type: ZoomRecurrenceType.WEEKLY,
      repeat_interval: preset === 'biweekly' ? 2 : 1,
      end_times: formData.repeatTimes,
      weekly_days: String(dayjsDayToZoomWeekday(dow)),
    };
  }
  if (preset === 'monthly') {
    if (formData.monthlyNth >= 5) {
      return undefined;
    }
    return {
      type: ZoomRecurrenceType.MONTHLY,
      repeat_interval: 1,
      end_times: formData.repeatTimes,
      monthly_week: formData.monthlyNth,
      monthly_week_day: dayjsDayToZoomWeekday(formData.monthlyWeekday),
    };
  }
  return undefined;
}
